import React, { useId, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Search, X, CornerDownLeft, SearchX, Map as MapIcon, Skull, MapPin, Swords, Gem, HelpCircle, BookOpen, Loader2, BoxIcon, ScrollText, FlaskConical, Trophy, UserRound, ArrowRight } from "lucide-react";
import { Highlight } from "./Highlight";
import { useGuideSearch } from "../../../hooks/useGuideSearch";
import { SearchGroup } from "../../../utils/guideSearch";
import { detectIntent } from "../../../utils/searchIntents";
import { useWorldLookup } from "../../../hooks/useWorldLookup";
import { EntityThumb } from "../../world/EntityThumb";
import { itemIdFromKey } from "../../../utils/worldFormat";
import type { EntityRef } from "../../../types/guide";

/** Respaldo para documentos sin entidad declarada: la ruta de su ficha contiene el ID exacto. */
const entityFromPath = (path: string): EntityRef | undefined => {
  const mid = /\/monstruo\/(\d+)/.exec(path)?.[1];
  if (mid) return {
    type: 'monster',
    id: Number(mid)
  };
  const key = /\/guia\/items\/([^/#?]+)/.exec(path)?.[1];
  return key ? {
    type: 'item',
    id: itemIdFromKey(decodeURIComponent(key))
  } : undefined;
};
type GuideSearchProps = {
  variant?: 'hero' | 'compact';
  suggestions?: string[];
};
const groupIcons: Record<SearchGroup, BoxIcon> = {
  Mapas: MapIcon,
  Monstruos: Skull,
  Spots: MapPin,
  Invasiones: Swords,
  'Ítems': Gem,
  'Preguntas frecuentes': HelpCircle,
  'Guías': BookOpen,
  Misiones: ScrollText,
  Recetas: FlaskConical,
  Eventos: Trophy,
  NPC: UserRound
};
export function GuideSearch({
  variant = 'compact',
  suggestions
}: GuideSearchProps) {
  const s = useGuideSearch();
  const listId = useId();
  const reduceMotion = useReducedMotion();
  const hero = variant === 'hero';
  const navigate = useNavigate();
  const {
    lookup
  } = useWorldLookup();
  const intent = useMemo(() => detectIntent(s.query, lookup), [s.query, lookup]);
  const showPanel = s.open && s.hasQuery;
  const openIntent = (to: string) => {
    navigate(to);
    s.setOpen(false);
    s.setQuery('');
    s.inputRef.current?.blur();
  };
  let index = -1;
  return <div className="relative" data-id="element-641" data-mp="1iomedqzt4zdj">
      <div className={`flex items-center gap-3 rounded-full border bg-[#0A1628] transition-colors duration-150 focus-within:border-[#D4AF37]/70 ${hero ? 'h-11 md:h-16 border-[#D4AF37]/40 px-4 md:px-6' : 'h-11 md:h-12 border-white/15 px-4'}`} data-id="element-642" data-mp="1imt6y9hdeb4h">
        <Search className={`shrink-0 text-[#D4AF37] ${hero ? 'h-4 w-4 md:h-6 md:w-6' : 'h-4 w-4'}`} aria-hidden="true" data-id="element-643" data-mp="qukedgooi2x6" />
        <input ref={s.inputRef} type="search" role="combobox" aria-expanded={showPanel} aria-controls={listId} aria-autocomplete="list" aria-activedescendant={showPanel && s.results[s.active] ? `${listId}-${s.active}` : undefined} aria-label="Buscar en la guía" value={s.query} onChange={e => {
        s.setQuery(e.target.value);
        s.setOpen(true);
      }} onFocus={() => s.setOpen(true)} onBlur={() => s.setOpen(false)} onKeyDown={s.onKeyDown} placeholder="Busca un ítem, mapa, evento o escribe tu pregunta…" className={`min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-sm placeholder:text-gray-500 focus:outline-none md:placeholder:text-base [&::-webkit-search-cancel-button]:hidden ${hero ? 'md:text-lg' : 'md:text-[15px]'}`} data-id="element-644" data-mp="zpryufsiruwb" />
        {s.query && <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => s.setQuery('')} aria-label="Borrar búsqueda" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-gray-400 transition-colors duration-150 hover:text-white" data-id="element-645" data-mp="3k7dqctzjy7ed">
            <X className="h-4 w-4" data-id="element-646" data-mp="1vh99dq0p5ch" />
          </button>}
      </div>

      {hero && suggestions && suggestions.length > 0 && <div className="-mx-4 mt-2.5 flex items-center gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:mt-4 md:flex-wrap md:gap-2 md:overflow-visible md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden" data-id="element-647" data-mp="6wwqsvszktq4">
          <span className="shrink-0 text-xs text-gray-500 md:text-sm" data-id="element-648" data-mp="37s7buyhy64y6">Prueba con:</span>
          {suggestions.map(q => <button key={q} type="button" onClick={() => s.fill(q)} className="shrink-0 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-gray-300 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white md:px-3 md:py-1.5 md:text-sm" data-id="element-649" data-mp="330xlfvokqxl8">
              {q}
            </button>)}
        </div>}

      <AnimatePresence data-id="element-650" data-mp="3qqa4dn9pek0q">
        {showPanel && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: reduceMotion ? 0 : 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute inset-x-0 z-40 mt-2 overflow-hidden rounded-3xl border border-white/10 bg-[#0A1628] shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${hero ? 'top-11 md:top-16' : 'top-11 md:top-12'}`} data-id="element-651" data-mp="3ls89m52x0w9f">
            {s.worldStatus === 'loading' && <p className="flex items-center gap-2 border-b border-white/5 px-5 py-2.5 text-xs text-gray-400" data-id="element-652" data-mp="fwuwsd81owrt">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#D4AF37]" aria-hidden="true" data-id="element-653" data-mp="kuwiz70rpc7i" />
                Cargando mapas, monstruos e ítems…
              </p>}
            {intent && <div className="border-b border-white/5 p-3" data-id="element-654" data-mp="ulhger4pgaou">
                <p className="px-1 pb-2 text-xs font-semibold text-[#D4AF37]" data-id="element-655" data-mp="umshgj6rn4c9">{intent.title}</p>
                <div className={`grid gap-2 ${intent.options.length === 2 ? 'sm:grid-cols-2' : intent.options.length > 2 ? 'grid-cols-2 sm:grid-cols-4' : ''}`} data-id="element-656" data-mp="zk14ax2uzwju">
                  {intent.options.map((o, i) => <button key={o.to} type="button" onMouseDown={e => e.preventDefault()} onClick={() => openIntent(o.to)} className={`group flex min-h-[44px] items-center justify-between gap-2 rounded-2xl border px-3 py-2 text-left transition-colors duration-150 ${i === 0 ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/15' : 'border-white/10 hover:border-[#D4AF37]/40'} ${intent.options.length > 2 && i === 0 ? 'col-span-2 sm:col-span-4' : ''}`} data-id="element-657" data-mp="18ls63qua3j8f" data-mpi={i}>
                      <span className="min-w-0" data-id="element-658" data-mp="339xati57zkva">
                        <span className="block text-sm font-semibold text-white" data-id="element-659" data-mp="33b8dtfqucolc">{o.label}</span>
                        {o.description && <span className="block text-xs text-gray-400" data-id="element-660" data-mp="3h6u19ihff0bn">{o.description}</span>}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-661" data-mp="3q9mtbnyfng2j" />
                    </button>)}
                </div>
              </div>}
            {s.results.length > 0 ? <div id={listId} role="listbox" aria-label="Resultados de la guía" className="max-h-[60vh] overflow-y-auto p-2" data-id="element-662" data-mp="3uafzcgqqfoh">
                {s.groups.map(g => {
            const Icon = groupIcons[g.group];
            return <div key={g.group} role="group" aria-label={g.group} className="mb-1 last:mb-0" data-id="element-663" data-mp="ds81vtb4o0dx">
                      <p className="px-3 pb-1 pt-2.5 text-xs font-semibold text-gray-500" data-id="element-664" data-mp="8twtzo4e7zyc">{g.group}</p>
                      {g.results.map(r => {
                index += 1;
                const i = index;
                const isActive = i === s.active;
                return <div key={r.doc.key} id={`${listId}-${i}`} role="option" aria-selected={isActive} onMouseDown={e => e.preventDefault()} onMouseEnter={() => s.setActive(i)} onClick={() => s.go(r)} className={`flex cursor-pointer gap-3 rounded-2xl px-3 py-3 transition-colors duration-100 ${isActive ? 'bg-white/[0.06]' : ''}`} data-id="element-665" data-mp="wenzyl15dxze">
                            {/* Imagen por la entidad del resultado (tipo + ID exacto), con el mismo componente que las fichas.
                                Sin acción de clic (no abre el resultado) y sin foco: el teclado sigue en el buscador.
                                El icono de la sección solo aparece si esa entidad no tiene imagen. */}
                            <span className="mt-0.5 shrink-0" data-id="element-666" data-mp="3lyhlrhb49rt6">
                              <EntityThumb entity={r.doc.entity ?? entityFromPath(r.doc.path)} name={r.doc.title} focusable={false} fallback={<span className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]" data-id="element-668" data-mp="a8efpafo4sdl">
                                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-669" data-mp="18hnwzxzfdeb" />
                                </span>} data-id="element-667" data-mp="3qx2bunwnmv3w" />
                            </span>
                            <span className="min-w-0 flex-1" data-id="element-670" data-mp="3sej75vs3pdb1">
                              <span className="block text-[15px] font-semibold leading-snug text-white" data-id="element-671" data-mp="3ng7tf9yoqinj">
                                <Highlight text={r.doc.title} terms={r.terms} data-id="element-672" data-mp="3io6lu1cgpg3u" />
                                {r.doc.sectionTitle && <span className="font-normal text-gray-400" data-id="element-673" data-mp="3eopdn05ni4uz"> › {r.doc.sectionTitle}</span>}
                              </span>
                              <span className="mt-0.5 block text-xs text-[#D4AF37]/80" data-id="element-674" data-mp="39mglu74il8ug">
                                {r.doc.typeLabel}
                                {r.doc.context ? ` · ${r.doc.context}` : ''}
                              </span>
                              <span className="mt-1.5 block text-sm leading-relaxed text-gray-400" data-id="element-675" data-mp="2pleymlrjm4jn">
                                <span className="text-gray-500" data-id="element-676" data-mp="2uki58an68itq">Coincide en {r.reasonLabel.toLowerCase()}: </span>
                                <Highlight text={r.snippet} terms={r.terms} data-id="element-677" data-mp="2zlxnjuxysb8f" />
                              </span>
                            </span>
                            {isActive && <CornerDownLeft className="mt-1 hidden h-4 w-4 shrink-0 text-gray-500 md:block" aria-hidden="true" data-id="element-678" data-mp="26wh1rg4unsl6" />}
                          </div>;
              })}
                    </div>;
          })}
              </div> : intent ? <div id={listId} role="status" className="sr-only" data-id="element-679" data-mp="2gt3iptu21zhx">Sin otros resultados</div> : <div id={listId} role="status" className="flex items-start gap-3 p-5" data-id="element-680" data-mp="2lld3kvgbc9dc">
                <SearchX className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-681" data-mp="1o5phvkn8kjsj" />
                <p className="text-[15px] leading-relaxed text-gray-300" data-id="element-682" data-mp="1t4al7sxsbjl7">
                  No encontramos información publicada sobre esa búsqueda. Prueba otro término o consulta al staff.
                </p>
              </div>}
            {s.results.length > 0 && <p className="hidden border-t border-white/5 px-5 py-2.5 text-xs text-gray-500 md:block" data-id="element-683" data-mp="2pkm036e4ez58">
                ↑ ↓ para moverte · Enter para abrir · Esc para cerrar
              </p>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}